import { useEffect, useState } from 'react';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { supabase } from '@/lib/supabase';

export default function About() {
  const [productCount, setProductCount] = useState<number | null>(null);
  const [guideCount, setGuideCount] = useState<number | null>(null);
  const [categoryCount, setCategoryCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      supabase.from('products').select('*', { count: 'exact', head: true }),
      supabase.from('buying_guides').select('*', { count: 'exact', head: true }),
      supabase.from('categories').select('*', { count: 'exact', head: true }),
    ]).then(([products, guides, categories]) => {
      if (cancelled) return;
      setProductCount(products.count ?? null);
      setGuideCount(guides.count ?? null);
      setCategoryCount(categories.count ?? null);
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white text-ink-900 antialiased">
      <Header />
      <main className="pt-28 md:pt-36 pb-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-ink-900 tracking-tight">About BuyWize</h1>
          <p className="mt-4 text-lg text-ink-600 leading-relaxed">
            We help shoppers make smarter buying decisions with honest, data-backed recommendations.
          </p>

          <div className="mt-10 space-y-8 text-ink-700 leading-relaxed">
            <section>
              <h2 className="font-display text-2xl font-bold text-ink-900 mb-3">Our Mission</h2>
              <p>
                BuyWize was created with a simple mission: take the guesswork out of online shopping. We research, compare, and write up products across every major category so you can buy with confidence. No sponsored placements, no hidden agendas. Just honest recommendations backed by real data.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-ink-900 mb-3">How We Evaluate Products</h2>
              <p>
                Every product on BuyWize is evaluated using its real specifications, verified current pricing, and genuine customer ratings and feedback, not marketing copy. We compare options within a category against each other before deciding what earns a recommendation, and we call out real tradeoffs and downsides rather than only listing positives.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-ink-900 mb-3">How We Make Money</h2>
              <p>
                BuyWize is reader-supported. When you buy through links on our site, we may earn an affiliate commission at no extra cost to you. This never influences our recommendations. Our reviews and ratings are determined solely by product quality and value, not by affiliate relationships.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-ink-900 mb-3">By the Numbers</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                {[
                  { n: productCount, l: 'Products Reviewed' },
                  { n: guideCount, l: 'Buying Guides' },
                  { n: categoryCount, l: 'Categories Covered' },
                ].map(s => (
                  <div key={s.l} className="bg-ink-50 rounded-2xl p-5 text-center">
                    <p className="font-display text-2xl font-bold text-cta-600">{s.n != null ? `${s.n}+` : '...'}</p>
                    <p className="text-xs text-ink-500 mt-1">{s.l}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}